import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Upload, Sparkles, FileSpreadsheet, Download, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Wand2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function PreviewTable({ columns, rows, testid }) {
  return (
    <div data-testid={testid} className="overflow-auto max-h-[420px] border border-zinc-200">
      <table className="control-table w-full">
        <thead>
          <tr>{columns.map((c, i) => <th key={`${i}-${c}`} className="whitespace-nowrap sticky top-0 bg-zinc-50">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className={`whitespace-nowrap max-w-[240px] truncate ${cell === null || cell === '' ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  {cell === null || cell === '' ? '—' : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PacerPage() {
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Unsupported format — Pacer accepts .xlsx or .xls Excel files only');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — the maximum allowed size is 10 MB`);
      return;
    }
    setProcessing(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/pacer/clean', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success('Cleanup complete');
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Processing failed — please try again');
    } finally {
      setProcessing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const download = async (fmt) => {
    try {
      const response = await api.get(`/pacer/download/${result.result_id}?format=${fmt}`, { responseType: 'blob' });
      const cd = response.headers['content-disposition'];
      const match = cd?.match(/filename=(.+)/);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? match[1] : `cleaned.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch {
      toast.error('Download failed — please re-run the cleanup');
    }
  };

  return (
    <div className="min-h-screen bg-white page-enter">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-emerald-600" />
            <h1 className="text-xl font-bold text-zinc-900" style={{ fontFamily: "'Chivo', sans-serif" }}>Pacer — Excel Cleanup</h1>
          </div>
          <p className="text-sm text-zinc-500 mt-1">Upload a messy Excel sheet, get back a neat cleaned version. Fully automatic.</p>
        </div>

        {/* Dropzone */}
        <div
          data-testid="pacer-dropzone"
          className={`border-2 border-dashed p-10 flex flex-col items-center justify-center cursor-pointer min-h-[170px] transition-all ${
            dragging ? 'border-emerald-400 bg-emerald-50/50' : 'border-zinc-300 bg-zinc-50/50 hover:border-emerald-400 hover:bg-emerald-50/30'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => !processing && fileRef.current?.click()}
        >
          <input ref={fileRef} data-testid="pacer-file-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
          {processing ? (
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              <span data-testid="pacer-processing" className="text-sm text-zinc-600 font-medium">Cleaning your sheet — removing junk, fixing formats...</span>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 text-zinc-400 mb-3" />
              <p className="text-sm text-zinc-600 font-medium">
                Drop your Excel file here or <span className="text-emerald-600 underline">browse</span>
              </p>
              <p className="text-xs text-zinc-400 mt-1">.xlsx or .xls — single sheet — max 10 MB</p>
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div data-testid="pacer-error" className="border border-red-200 bg-red-50/60 p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700">{error}</p>
              <p className="text-xs text-red-500 mt-0.5">Fix the file and drop it again — the uploader is ready.</p>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4 animate-fade-in">
            <div data-testid="pacer-summary" className="border border-emerald-200 bg-emerald-50/50 p-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-zinc-900">{result.summary}</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {result.ai_used
                    ? <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-emerald-600" /> AI cleanup applied (conservative — no values invented)</span>
                    : 'Rule-based cleanup only — AI pass was not needed'}
                </p>
              </div>
            </div>

            {/* Downloads */}
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="pacer-download-xlsx" onClick={() => download('xlsx')} className="rounded-none bg-emerald-600 hover:bg-emerald-700 h-10">
                <Download className="h-4 w-4 mr-2" /> Download Cleaned .xlsx
              </Button>
              <Button data-testid="pacer-download-csv" variant="outline" onClick={() => download('csv')} className="rounded-none border-zinc-300 h-10">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Cleaned .csv
              </Button>
              <Button data-testid="pacer-download-json" variant="outline" onClick={() => download('json')} className="rounded-none border-zinc-300 h-10">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Cleaned .json
              </Button>
            </div>

            {/* Cleanup report */}
            <div className="border border-zinc-200">
              <button
                data-testid="pacer-report-toggle"
                onClick={() => setReportOpen(!reportOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-50 transition-colors"
              >
                <span className="text-xs font-semibold tracking-[0.08em] uppercase text-zinc-500">
                  Cleanup Report ({result.report.length} changes)
                </span>
                {reportOpen ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
              </button>
              {reportOpen && (
                <ul data-testid="pacer-report-list" className="border-t border-zinc-200 divide-y divide-zinc-100 max-h-[260px] overflow-y-auto">
                  {result.report.map((line, i) => (
                    <li key={`${i}-${line}`} className={`px-4 py-2 text-xs flex items-start gap-2 ${line.startsWith('AI:') ? 'text-emerald-700 bg-emerald-50/30' : 'text-zinc-600'}`}>
                      {line.startsWith('AI:') ? <Sparkles className="h-3 w-3 mt-0.5 shrink-0" /> : <CheckCircle2 className="h-3 w-3 mt-0.5 text-zinc-300 shrink-0" />}
                      {line}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Before / After preview */}
            <Tabs defaultValue="cleaned">
              <TabsList className="rounded-none h-9">
                <TabsTrigger data-testid="pacer-tab-original" value="original" className="rounded-none text-xs px-4">Before (Original)</TabsTrigger>
                <TabsTrigger data-testid="pacer-tab-cleaned" value="cleaned" className="rounded-none text-xs px-4">After (Cleaned)</TabsTrigger>
              </TabsList>
              <TabsContent value="original" className="mt-2">
                <p className="text-[11px] text-zinc-400 mb-1.5">Raw sheet as uploaded (first 50 rows) — view only, not downloadable</p>
                <PreviewTable columns={result.original_preview.columns} rows={result.original_preview.rows} testid="pacer-original-table" />
              </TabsContent>
              <TabsContent value="cleaned" className="mt-2">
                <p className="text-[11px] text-zinc-400 mb-1.5">Cleaned output (first 50 rows)</p>
                <PreviewTable columns={result.cleaned_preview.columns} rows={result.cleaned_preview.rows} testid="pacer-cleaned-table" />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>
    </div>
  );
}
